import type { ArtifactKind } from '@api/processes';
import type { SelectFieldOption } from './nodes/SelectField';

/**
 * The user-pickable artifact kinds for an output port, mirroring the
 * backend's {@code PortTypeOptions.OUTPUT_VALUES} (single source of truth
 * on the catalog side; this list keeps the FE-only Task per-port kind
 * picker in sync without round-tripping through {@code ConfigFieldDto}).
 *
 * <p>Narrower than {@code ArtifactKind}. The omitted kinds stay valid on the
 * wire, so a legacy port still round-trips — it just can't be re-picked.</p>
 *
 * <p>Order matches the backend list so the dropdown rendering is
 * identical between the per-port picker and the body
 * {@code dataType} field.</p>
 */
export const OUTPUT_PORT_KIND_OPTIONS: readonly SelectFieldOption[] = [
  { value: 'MODEL_3D', label: '3D Model' },
  { value: 'IMAGE', label: 'Image' },
  { value: 'DOCUMENT', label: 'File' },
  { value: 'GCODE', label: 'G-code' },
  { value: 'TEXT', label: 'Text' },
];

/** Type guard ensuring a string is one of the user-pickable kinds.
 *  Used by callers that need to round-trip an arbitrary string through
 *  the picker (e.g. wire payloads that may carry {@code NONE} from
 *  legacy seeds) without falling off the type-safety cliff. */
export function isPickableOutputKind(value: string): value is ArtifactKind {
  return OUTPUT_PORT_KIND_OPTIONS.some((o) => o.value === value);
}
